import moment from 'moment';
import { formatCardExpirationDate } from 'renderer/shared/formatters/formatters';
import { TransactionDetails } from './transaction.schemas';

export const isPayPalTransaction = (transaction: TransactionDetails): boolean =>
  transaction.paymentProvider?.toLowerCase() === 'paypal' ||
  Boolean(transaction.paypalId) ||
  transaction.embeddedPaymentMethod?.provider?.toLowerCase() === 'paypal' ||
  transaction.embeddedPaymentMethod?.cardType?.toLowerCase() === 'paypal';

export type TransactionProcessor = 'paypal' | 'stripe' | 'braintree';

export const getTransactionProcessor = (
  transaction: TransactionDetails,
): TransactionProcessor | null => {
  if (isPayPalTransaction(transaction)) {
    return 'paypal';
  }

  const provider = transaction.paymentProvider?.toLowerCase();

  if (
    provider === 'stripe' ||
    transaction.stripeId ||
    transaction.embeddedPaymentMethod?.stripeId
  ) {
    return 'stripe';
  }

  if (
    provider === 'braintree' ||
    transaction.braintreeTransactionId ||
    transaction.embeddedPaymentMethod?.braintreeId
  ) {
    return 'braintree';
  }

  return null;
};

export const getTransactionPaymentMethodLabel = (
  transaction: TransactionDetails,
): string | null => {
  if (isPayPalTransaction(transaction)) {
    return 'PayPal';
  }

  const { embeddedPaymentMethod, paymentProvider } = transaction;

  if (embeddedPaymentMethod?.cardType) {
    const { cardType, last4 } = embeddedPaymentMethod;
    return last4 ? `${cardType} •••• ${last4}` : cardType;
  }

  if (paymentProvider) {
    return paymentProvider;
  }

  return null;
};

export const getTransactionPaymentMethodExpiration = (
  transaction: TransactionDetails,
): string | null => {
  const { expirationMonth, expirationYear } =
    transaction.embeddedPaymentMethod ?? {};

  if (!expirationMonth || !expirationYear) {
    return null;
  }

  return formatCardExpirationDate(expirationMonth, expirationYear);
};

export const getProductDescription = (transaction: TransactionDetails) => {
  const {
    productName,
    hostApplication,
    category,
    upgradeOrDowngrade,
    platform,
  } = transaction;
  let name = `${productName} `;
  let productPlatform = '';
  if (platform) {
    productPlatform = `on ${platform}`;
  }
  if (upgradeOrDowngrade) {
    name = `${upgradeOrDowngrade} ${productName ? `for ${productName} ` : ''}`;
  }
  // without host app
  if (!hostApplication) {
    return name + productPlatform;
  }
  // with host app
  const { displayName, version } = hostApplication;
  const productHostApp = `for ${displayName} `;
  let productVersion = '';
  if (category !== 'Subscription' && category !== 'Bundle') {
    productVersion = `${version} `;
  }
  return name + productVersion + productHostApp + productPlatform;
};
export const transformTransactionToInvoice = (
  transactions: TransactionDetails[],
) => {
  const first = transactions[0];
  let totalSavings = 0;
  let subtotal = 0;
  let total = 0;
  let discountAmount = 0;
  let discountCode: string | undefined;
  const serialKeyList: string[] = [];

  const items = transactions.map((t) => {
    let unitSavings = 0;
    let itemTotalSavings = 0;

    if (t.prices?.salePrice && t.quantity) {
      unitSavings = t.prices.startingPrice - t.prices.salePrice;
      itemTotalSavings = unitSavings * t.quantity;
      totalSavings += itemTotalSavings;
    }

    subtotal += t.prices?.subtotal ?? 0;
    total += t.amount ?? 0;

    // Aggregate discount amount
    if (t.prices?.discountDetails?.amount) {
      discountAmount += t.prices.discountDetails.amount;
    }

    // Use first available discount code (strip internal apportioned suffixes)
    if (!discountCode && t.prices?.discountDetails?.code) {
      discountCode = t.prices.discountDetails.code
        .trim()
        .replace(/-apportioned-.*$/, '');
    }

    // Accumulate serials
    if (t.serials?.length) {
      serialKeyList.push(...t.serials);
    }

    return {
      description: getProductDescription(t),
      unitPrice: t.prices?.totalPrice,
      quantity: t.quantity,
      total: t.amount,
      serials: t.serials,
      serialGroups: t.serialGroups,
      savings: itemTotalSavings,
      startingPrice: t.prices?.startingPrice,
      subtotal: t.prices?.subtotal,
    };
  });

  return {
    id: first.cartId ?? first._id,
    orderDate: moment(first.createdAt).format('MM/DD/YYYY'),
    customer: {
      name: `${first.firstName} ${first.lastName}`,
      deliveryAddress: first.embeddedPaymentMethod?.billingAddress
        ? {
            street: first.embeddedPaymentMethod.billingAddress.streetAddress,
            city: first.embeddedPaymentMethod.billingAddress.locality,
            state: first.embeddedPaymentMethod.billingAddress.region,
            zip: first.embeddedPaymentMethod.billingAddress.postalCode,
            country: first.embeddedPaymentMethod.billingAddress.countryName,
          }
        : undefined,
      billingAddress: first.embeddedPaymentMethod?.billingAddress
        ? {
            street: first.embeddedPaymentMethod.billingAddress.streetAddress,
            city: first.embeddedPaymentMethod.billingAddress.locality,
            state: first.embeddedPaymentMethod.billingAddress.region,
            zip: first.embeddedPaymentMethod.billingAddress.postalCode,
            country: first.embeddedPaymentMethod.billingAddress.countryName,
          }
        : undefined,
    },
    items,
    upgradeOrDowngrade: first.upgradeOrDowngrade,
    subtotal,
    total,
    discountAmount,
    discountCode,
    paymentMethod: first.embeddedPaymentMethod?.cardType,
    carrier: 'Electronic Delivery',
    serialKey: serialKeyList.join(','),
    totalSavings,
  };
};

export type InvoiceDetails = ReturnType<typeof transformTransactionToInvoice>;
